import { useParams, useRoute } from 'wouter-preact'
import { clsx } from 'clsx'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { useComputed, useSignal } from '@preact/signals'

import {
	HorizontalDivider,
	KeyboardArrowDownIcon,
	Typography,
	VerticalDivider
} from 'components/UI/atoms'
import { projectDetailsState, projectsState } from 'state'

import { DropdownItem } from './DropdownItem'
import { ExitWorkspaceModal } from './ExitWorkspaceModal'

const Dropdown = () => {
	// Dropdown component is outside the dApp routes scope.
	// This means that we don't have access to the dappName params from useParams.
	// We can use useRoute to get the dappName from the URL.
	const [match, params] = useRoute('/smart-contract/:dAppName/*')
	const [isOnWorkspacePage] = useRoute('/smart-contract/:dAppName/workspace')
	const { projectId } = useParams()

	const isExitWorkspaceModalOpen = useSignal(false)

	const handleCloseExitWorkspaceModal = () => {
		isExitWorkspaceModalOpen.value = false
	}

	const handleOpenExitWorkspaceModal = () => {
		isExitWorkspaceModalOpen.value = true
	}

	const projectName = useComputed(() => {
		if (!projectDetailsState.value?.name) return ''

		const projectNames = projectsState.value.filter(
			project => project.name === projectDetailsState.value?.name
		)

		if (projectNames.length >= 2) {
			return `${projectDetailsState.value.name} (${projectDetailsState.value.publicId})`
		}

		return projectDetailsState.value.name
	})

	if (!isOnWorkspacePage) {
		return null
	}

	return (
		<>
			<div class='flex'>
				{isOnWorkspacePage && (
					<>
						<div>
							<Typography
								class='inline-block'
								color='text-product'
								text='Workspace'
								variant='h5'
							/>
						</div>
						<VerticalDivider className='mx-4' />
					</>
				)}

				<Popover class='relative'>
					{projectDetailsState.value?.name && (
						<PopoverButton
							aria-label='Toggle menu'
							class='inline-flex items-center gap-x-2 focus-visible:outline-none'
						>
							<div>
								<Typography
									class='inline-block cursor-pointer first-letter:uppercase'
									color='text-black-100'
									text={projectName.value}
									variant='label2'
									weight='font-normal'
								/>

								<span class={clsx(!match && 'hidden')}>/</span>

								{params?.dAppName && (
									<Typography
										class='cursor-pointer capitalize'
										color='text-black-100'
										text={params.dAppName}
										variant='label2'
										weight='font-normal'
									/>
								)}
							</div>

							<KeyboardArrowDownIcon />
						</PopoverButton>
					)}

					<PopoverPanel
						anchor='bottom'
						class='
          absolute z-10 mt-2 w-56 origin-top transform
          rounded-md border border-black-500 bg-black-900 shadow-lg
          transition
          duration-200 ease-in-out
          focus:outline-none data-[closed]:-translate-y-1 data-[closed]:opacity-0
        '
						transition
					>
						{({ close }) => (
							<>
								<DropdownItem
									closeMenu={close}
									href='/'
									hrefNewTab={`${window.location.origin}/dapp/${projectId}`}
									title='Dashboard'
								/>
								<DropdownItem
									closeMenu={close}
									href='/settings'
									hrefNewTab={`${window.location.origin}/dapp/${projectId}/settings`}
									title='Settings'
								/>
								<HorizontalDivider />
								<DropdownItem
									closeMenu={close}
									href='~/'
									hrefNewTab={window.location.origin}
									title='Go to Home'
								/>
								<DropdownItem
									onClick={handleOpenExitWorkspaceModal}
									title='Exit workspace'
								/>
							</>
						)}
					</PopoverPanel>
				</Popover>
			</div>

			{isExitWorkspaceModalOpen.value && (
				<ExitWorkspaceModal onCloseModal={handleCloseExitWorkspaceModal} />
			)}
		</>
	)
}

export { Dropdown }
